import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { ProgressSpinner } from "primereact/progressspinner";
import ResponsiveView from "../../components/responsive/ResponsiveView";
import DataCard from "../../components/responsive/DataCard";
import DualCurrencyValue from "../../components/money/DualCurrencyValue";
import { fetchAdminCashSummary, formatCurrency } from "../../services/cashService";
import styles from "../shifts/shifts.module.scss";

const RANGE_OPTIONS = [
    { label: "All Time", value: "all" },
    { label: "Today", value: "day" },
    { label: "This Week", value: "week" },
    { label: "This Month", value: "month" },
];

const staffName = (staff) => (staff ? `${staff.firstName || ""} ${staff.lastName || ""}`.trim() || staff.email : "Unknown");

const AdminCashSummary = () => {
    const [range, setRange] = useState("all");
    const [dateRange, setDateRange] = useState(null);
    const [loading, setLoading] = useState(true);
    const [staffCash, setStaffCash] = useState([]);
    const [ownerWire, setOwnerWire] = useState([]);
    const [grandTotal, setGrandTotal] = useState(null);

    const loadSummary = async () => {
        setLoading(true);
        let res;
        if (dateRange && dateRange.length === 2 && dateRange[1]) {
            res = await fetchAdminCashSummary(null, dateRange[0].toISOString(), dateRange[1].toISOString());
        } else {
            res = await fetchAdminCashSummary(range);
        }
        setStaffCash(res?.data?.cashSummary || []);
        setOwnerWire(res?.data?.wireSummary || []);
        setGrandTotal(res?.data?.totals || null);
        setLoading(false);
    };

    useEffect(() => {
        loadSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range, dateRange]);

    const staffBody = (row) => staffName(row.staff);
    const cashNetBody = (row) => <DualCurrencyValue pair={row.cashNet} compact />;
    // Discrepancy isn't a scalar on the staff row - a staff member can have
    // multiple shifts within the queried range, each with its own
    // cashDiscrepancy (a { usd, jmd } pair), so this sums across all of them
    // per-currency.
    const discrepancyBody = (row) => {
        const pair = (row.shifts || []).reduce(
            (sum, s) => ({ usd: sum.usd + (s.cashDiscrepancy?.usd || 0), jmd: sum.jmd + (s.cashDiscrepancy?.jmd || 0) }),
            { usd: 0, jmd: 0 },
        );
        return <DualCurrencyValue pair={pair} compact danger={Math.abs(pair.usd) >= 0.01 || Math.abs(pair.jmd) >= 0.01} />;
    };
    const ownerBody = (row) => row.owner || row.paymentOwner || "-";
    // wireNative is the actual currency breakdown (a wire can be JMD too,
    // same as cash); wireTotal is the USD-equivalent blended figure used
    // only for the grand total math.
    const wireTotalBody = (row) => <DualCurrencyValue pair={row.wireNative} compact />;
    const invoicesBody = (row) => {
        const invoices = row.invoices || [];
        if (!invoices.length) return "-";
        return invoices.map((inv) => inv.invoiceNo || inv.invoiceId || inv._id).join(", ");
    };

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div className={styles.headerTitleRow}>
                    <div className={styles.headerIcon}>
                        <i className="pi pi-chart-bar" />
                    </div>
                    <div>
                        <h2>Cash & Wire Summary</h2>
                        <p className={styles.subtitle}>Per-staff cash net and shift discrepancies, plus wire transfer totals by owner</p>
                    </div>
                </div>
            </div>

            <div className={styles.filtersSection}>
                <div className={styles.filterGroup}>
                    <label>Quick Range</label>
                    <div className={styles.quickRangeRow}>
                        {RANGE_OPTIONS.map((opt) => (
                            <Button
                                key={opt.value}
                                label={opt.label}
                                className={`${styles.quickRangeBtn} ${range === opt.value && !dateRange ? styles.activeRange : ""}`}
                                onClick={() => {
                                    setDateRange(null);
                                    setRange(opt.value);
                                }}
                            />
                        ))}
                    </div>
                </div>
                <div className={styles.filterGroup}>
                    <label htmlFor="dateRange">Custom Range</label>
                    <Calendar id="dateRange" value={dateRange} onChange={(e) => setDateRange(e.value)} selectionMode="range" readOnlyInput placeholder="Select date range" />
                </div>
                <div className={styles.filterGroup}>
                    <Button label="Apply" icon="pi pi-search" onClick={loadSummary} className={styles.primaryButton} />
                </div>
            </div>

            {loading ? (
                <div className={styles.loadingState}>
                    <ProgressSpinner strokeWidth="4" />
                    <span>Loading summary...</span>
                </div>
            ) : (
                <>
                    {grandTotal && (
                        <div className={styles.statsRow}>
                            <div className={styles.statCard}>
                                <div className={styles.statIcon}>
                                    <i className="pi pi-money-bill" />
                                </div>
                                <div>
                                    <DualCurrencyValue pair={grandTotal.cash} />
                                    <div className={styles.statLabel}>Total Cash</div>
                                </div>
                            </div>
                            <div className={styles.statCard}>
                                <div className={styles.statIcon}>
                                    <i className="pi pi-send" />
                                </div>
                                <div>
                                    <DualCurrencyValue pair={grandTotal.wire} />
                                    <div className={styles.statLabel}>Total Wire</div>
                                </div>
                            </div>
                            <div className={styles.statCard}>
                                <div className={styles.statIcon}>
                                    <i className="pi pi-wallet" />
                                </div>
                                <div>
                                    <div className={styles.statValue}>{formatCurrency(grandTotal.grandTotalUsd)}</div>
                                    <div className={styles.statLabel}>Grand Total (USD-equivalent)</div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={styles.sectionCardHeader} style={{ marginTop: "8px" }}>
                        <h3>
                            <i className="pi pi-users" />
                            Per-Staff Cash Net &amp; Discrepancies
                        </h3>
                    </div>
                    <ResponsiveView
                        value={staffCash}
                        emptyMessage="No cash activity for this period."
                        storageKey="cashSummary.staff"
                        renderCard={(row, i) => (
                            <DataCard
                                key={row.staff?._id || i}
                                title={staffBody(row)}
                                fields={[
                                    { label: "Cash Net", value: cashNetBody(row) },
                                    { label: "Discrepancy", value: discrepancyBody(row) },
                                ]}
                            />
                        )}
                    >
                        <DataTable value={staffCash} paginator rows={20} className="datatable-responsive custom-datatable" emptyMessage="No cash activity for this period." rowHover>
                            <Column header="Staff" body={staffBody} />
                            <Column header="Cash Net" body={cashNetBody} />
                            <Column header="Shift Discrepancy" body={discrepancyBody} />
                        </DataTable>
                    </ResponsiveView>

                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-send" />
                            Per-Owner Wire Transfer Totals
                        </h3>
                    </div>
                    <ResponsiveView
                        value={ownerWire}
                        emptyMessage="No wire transfers for this period."
                        storageKey="cashSummary.wire"
                        renderCard={(row, i) => (
                            <DataCard
                                key={i}
                                title={wireTotalBody(row)}
                                subtitle={ownerBody(row)}
                                fields={[{ label: "Invoices", value: invoicesBody(row) }]}
                            />
                        )}
                    >
                        <DataTable value={ownerWire} paginator rows={20} className="datatable-responsive custom-datatable" emptyMessage="No wire transfers for this period." rowHover>
                            <Column header="Owner" body={ownerBody} />
                            <Column header="Wire Total" body={wireTotalBody} />
                            <Column header="Linked Invoices" body={invoicesBody} />
                        </DataTable>
                    </ResponsiveView>
                </>
            )}
        </div>
    );
};

export default AdminCashSummary;
